import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  UserProfile,
  UserSocial,
  ProfileResponse,
  Level,
} from "../../types/profile";
import { AVAX_API } from "../../services/api/indexV2";

interface ProfileState {
  user: UserProfile | null;
  userSocial: UserSocial[];
  level: Level | null;
  point: number;
  next_level?: Level | null;
  ranking: number;
  loading: boolean;
  error: string | null;
  trade_volume: number;
}

const initialState: ProfileState = {
  user: null,
  userSocial: [],
  level: null,
  point: 0,
  next_level: null,
  ranking: 0,
  loading: false,
  error: null,
  trade_volume: 0
};

export const fetchUserProfile = createAsyncThunk(
  "profile/fetchUserProfile",
  async () => {
    const res = await AVAX_API.getProfile();
    return res.data as ProfileResponse;
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfile(state) {
      state.user = null;
      state.userSocial = [];
      state.level = null;
      state.point = 0;
      state.next_level = null;
      state.ranking = 0;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchUserProfile.fulfilled,
        (state, action: PayloadAction<ProfileResponse>) => {
          state.user = action.payload.user;
          state.userSocial = action.payload.userSocial;
          state.level = action.payload.level;
          state.point = action.payload.point;
          state.next_level = action.payload.next_level;
          state.ranking = action.payload.ranking;
          state.trade_volume = action.payload.trade_volume;
          state.loading = false;
        }
      )
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load profile";
      });
  },
});

export const { clearProfile } = profileSlice.actions;
export default profileSlice.reducer;

export const selectUserProfile = (state: RootState) => ({
  user: state.profile.user,
  userSocial: state.profile.userSocial,
});

export const selectUserStats = (state: RootState) => ({
  level: state.profile.level,
  point: state.profile.point,
  next_level: state.profile.next_level,
  ranking: state.profile.ranking,
  trade_volume: state.profile.trade_volume
});

export const selectUserProfileLoading = (state: RootState) =>
  state.profile.loading;
